// src/components/Navbar.jsx
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getSession, clearToken } from "../auth";

// Liens supplémentaires visibles selon le rôle connecté (jamais les routes
// /live/... — celles-ci restent accessibles uniquement via leur propre lien
// direct, pas depuis la navigation générale).
const ROLE_NAV_ITEMS = {
    chronometreur: [
        { label: "Protocole", path: "/protocole" },
        { label: "Timer", path: "/timer" },
        { label: "Timeserver", path: "/timeserver" },
        { label: "Vue d'ensemble lives", path: "/live-overview" },
        { label: "Courses parallèles", path: "/parallel" },
        { label: "Timing Software", path: "/timingsoftware" },
    ],
    manuel: [{ label: "Protocole", path: "/protocole" }],
    colour: [{ label: "Courses parallèles", path: "/parallel" }],
    paralleltimer: [{ label: "Courses parallèles", path: "/parallel" }],
};

export default function Navbar() {
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [session, setSession] = useState(getSession());

    const isAuth = !!session;
    const role = session?.role;

    // Recalcule la session affichée après une connexion/déconnexion dans le
    // même onglet (changement de route) ou dans un autre onglet (storage).
    useEffect(() => {
        setSession(getSession());
    }, [location]);

    useEffect(() => {
        const handleStorage = () => setSession(getSession());
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const handleLogout = () => {
        clearToken();
        setSession(null);
        navigate("/");
    };

    // Une fois connecté, on épure la navigation : plus de pages publiques
    // (Accueil, Prestations, À propos, Jobs) — seulement Calendrier + les
    // outils propres au rôle, pour aller droit au but.
    const navItems =
        isAuth && role
            ? [{ label: "Calendrier", path: "/calendrier" }, ...(ROLE_NAV_ITEMS[role] || [])]
            : [
                { label: "Accueil", path: "/" },
                { label: "Calendrier", path: "/calendrier" },
                { label: "Prestations", path: "/prestations" },
                { label: "À propos", path: "/a-propos" },
                { label: "Jobs", path: "/jobs" },
            ];

    return (
        <>
            <AppBar
                position="static"
                color="transparent"
                elevation={0}
                sx={{
                    borderBottom: "1px solid #e5e5e5",
                    backgroundColor: "#ffffff",
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>

                        {/* Logo */}
                        <Box
                            component="img"
                            src="../logo/logo.png"
                            alt="321 Timing"
                            height={55}
                            sx={{ cursor: "pointer" }}
                            onClick={() => (window.location.href = "/")}
                        />

                        {/* Desktop Navigation */}
                        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
                            {navItems.map((item) => (
                                <Button
                                    key={item.path}
                                    component={Link}
                                    to={item.path}
                                    sx={{
                                        color:
                                            location.pathname === item.path
                                                ? "secondary.main"
                                                : "primary.main",
                                        fontWeight: location.pathname === item.path ? 700 : 400,
                                        mx: 1,
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}

                            {isAuth ? (
                                <Button
                                    onClick={handleLogout}
                                    startIcon={<LogoutIcon />}
                                    sx={{ color: "primary.main", mx: 1 }}
                                >
                                    Déconnexion
                                </Button>
                            ) : (
                                <Button
                                    component={Link}
                                    to="/login"
                                    sx={{ color: "primary.main", mx: 1 }}
                                >
                                    Connexion
                                </Button>
                            )}
                        </Box>

                        {/* Mobile Menu Button */}
                        <IconButton
                            sx={{ display: { xs: "block", md: "none" } }}
                            onClick={() => setOpen(true)}
                        >
                            <MenuIcon sx={{ color: "primary.main" }} />
                        </IconButton>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Drawer Mobile */}
            <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
                <Box sx={{ width: 250, mt: 4 }}>
                    <List>
                        {navItems.map((item) => (
                            <ListItem key={item.path} disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to={item.path}
                                    onClick={() => setOpen(false)}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            color:
                                                location.pathname === item.path
                                                    ? "secondary.main"
                                                    : "primary.main",
                                            fontWeight: location.pathname === item.path ? 700 : 400,
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}

                        <Divider sx={{ my: 1 }} />

                        {isAuth ? (
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        setOpen(false);
                                        handleLogout();
                                    }}
                                >
                                    <ListItemText primary="Déconnexion" />
                                </ListItemButton>
                            </ListItem>
                        ) : (
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to="/login"
                                    onClick={() => setOpen(false)}
                                >
                                    <ListItemText primary="Connexion" />
                                </ListItemButton>
                            </ListItem>
                        )}
                    </List>
                </Box>
            </Drawer>
        </>
    );
}